import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  CalendarDays, 
  Clock, 
  MapPin,
  User,
  Mail,
  Phone,
  Home,
  Check,
  ChevronLeft,
  ChevronRight,
  ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, isToday, isBefore } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
  { id: "studio", label: "Studio" },
  { id: "1bed", label: "1 Bedroom" },
  { id: "2bed", label: "2 Bedrooms" },
  { id: "3bed", label: "3 Bedrooms" },
  { id: "4bed", label: "4 Bedrooms" },
  { id: "5plus", label: "5+ Bedrooms" }
];

const furnishedOptions = [
  { id: "unfurnished", label: "Unfurnished" },
  { id: "part-furnished", label: "Part Furnished" },
  { id: "furnished", label: "Fully Furnished" }
];

const serviceOptions = [
  { id: "inventory", label: "Inventory Report" },
  { id: "check-in", label: "Check-In" },
  { id: "check-out", label: "Check-Out" },
  { id: "mid-term", label: "Mid-Term Inspection" },
  { id: "snagging", label: "Snagging Survey" }
];

const BookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quoteData = location.state?.quote;
  const quoteFormData = location.state?.formData;

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingId, setBookingId] = useState("");

  const [formData, setFormData] = useState({
    name: quoteFormData?.name || "",
    email: quoteFormData?.email || "",
    phone: quoteFormData?.phone || "",
    property_address: "",
    postcode: quoteFormData?.postcode || "",
    property_type: quoteFormData?.property_type || "",
    furnished: quoteFormData?.furnished || "",
    services: quoteFormData?.services || [],
    notes: ""
  });

  // Generate calendar days
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  const fetchAvailability = async (dateStr) => {
    setIsLoadingSlots(true);
    try {
      const response = await axios.get(`${API}/availability/${dateStr}`);
      setAvailableSlots(response.data.available_slots);
    } catch (error) {
      toast.error("Failed to fetch availability");
      setAvailableSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handleDateSelect = (date) => {
    if (isBefore(date, new Date()) && !isToday(date)) return;
    setSelectedDate(date);
    setSelectedTime("");
  };

  const handleServiceToggle = (serviceId) => {
    const services = formData.services.includes(serviceId)
      ? formData.services.filter(s => s !== serviceId)
      : [...formData.services, serviceId];
    setFormData({ ...formData, services });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Please select a date and time");
      return;
    }

    if (!formData.name || !formData.email || !formData.phone || !formData.property_address) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.services.length === 0) {
      toast.error("Please select at least one service");
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingData = {
        ...formData,
        booking_date: format(selectedDate, "yyyy-MM-dd"),
        booking_time: selectedTime,
        quoted_price: quoteData?.total_price || 0
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingId(response.data.id);
      setBookingComplete(true);
      toast.success("Booking confirmed!");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to create booking");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (bookingComplete) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#B8960B]/20 flex items-center justify-center mx-auto mb-6">
            <Check className="text-[#B8960B]" size={40} />
          </div>
          <h1 className="text-3xl font-bold mb-4">Booking Confirmed!</h1>
          <p className="text-neutral-400 mb-2">Your booking reference:</p>
          <p className="text-xl font-mono text-[#B8960B] mb-6">{bookingId.slice(0, 8).toUpperCase()}</p>
          <p className="text-neutral-400 mb-8">
            We'll send a confirmation email to <span className="text-white">{formData.email}</span> with all the details.
          </p>
          <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-6 mb-8 text-left">
            <h3 className="font-semibold mb-4">Booking Details</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-neutral-500">Date</span>
                <span>{format(selectedDate, "EEEE, MMMM d, yyyy")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Time</span>
                <span>{selectedTime}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-neutral-500">Services</span>
                <span>{formData.services.length} selected</span>
              </div>
              {quoteData && (
                <div className="flex justify-between pt-2 border-t border-neutral-800">
                  <span className="text-neutral-500">Estimated Price</span>
                  <span className="text-[#B8960B] font-semibold">£{quoteData.total_price.toFixed(2)}</span>
                </div>
              )}
            </div>
          </div>
          <Link to="/">
            <Button className="btn-primary rounded-full px-8 py-6">
              Return to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="booking-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-14 w-auto"
              />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-neutral-400 hover:text-white">
                <ArrowLeft className="mr-2" size={18} /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neutral-900 border border-neutral-800 mb-6">
              <CalendarDays className="text-[#B8960B]" size={28} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Book Your Inspection</h1>
            <p className="text-neutral-400">Select a date and time that works for you</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Calendar Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6"
            >
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <button 
                  onClick={() => setCurrentMonth(addDays(monthStart, -1))}
                  className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <h3 className="text-lg font-semibold">
                  {format(currentMonth, "MMMM yyyy")}
                </h3>
                <button 
                  onClick={() => setCurrentMonth(addDays(monthEnd, 1))}
                  className="p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                  <div key={day} className="text-center text-xs text-neutral-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {/* Empty cells for days before month starts */}
                {Array.from({ length: (monthStart.getDay() + 6) % 7 }).map((_, i) => (
                  <div key={`empty-${i}`} className="aspect-square" />
                ))}
                
                {calendarDays.map(day => {
                  const isPast = isBefore(day, new Date()) && !isToday(day);
                  const isSelected = selectedDate && isSameDay(day, selectedDate);
                  const isCurrentDay = isToday(day);
                  
                  return (
                    <button
                      key={day.toISOString()}
                      onClick={() => handleDateSelect(day)}
                      disabled={isPast}
                      className={`aspect-square rounded-lg flex items-center justify-center text-sm transition-all ${
                        isPast 
                          ? 'text-neutral-700 cursor-not-allowed' 
                          : isSelected
                            ? 'bg-[#B8960B] text-white'
                            : isCurrentDay
                              ? 'bg-neutral-800 text-white'
                              : 'hover:bg-neutral-800 text-neutral-300'
                      }`}
                      data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                    >
                      {format(day, "d")}
                    </button>
                  );
                })}
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div className="mt-6 pt-6 border-t border-neutral-800">
                  <h4 className="text-sm font-semibold text-neutral-400 mb-4 flex items-center gap-2">
                    <Clock size={16} />
                    Available Times for {format(selectedDate, "MMMM d")}
                  </h4>
                  
                  {isLoadingSlots ? (
                    <div className="text-center py-4 text-neutral-500">Loading...</div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-4 text-neutral-500">No available slots for this date</div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {availableSlots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`py-3 rounded-lg text-sm font-medium transition-all ${
                            selectedTime === slot
                              ? 'bg-[#B8960B] text-white'
                              : 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300'
                          }`}
                          data-testid={`time-slot-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </motion.div>

            {/* Booking Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                {/* Contact Details */}
                <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <User size={18} className="text-[#B8960B]" />
                    Contact Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-neutral-400 text-sm">Name *</Label>
                      <Input 
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="bg-neutral-950 border-neutral-800 mt-1"
                        placeholder="Your name"
                        required
                        data-testid="booking-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-neutral-400 text-sm">Email *</Label>
                      <Input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="bg-neutral-950 border-neutral-800 mt-1"
                        placeholder="your@email.com"
                        required
                        data-testid="booking-email-input"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-neutral-400 text-sm">Phone *</Label>
                      <Input 
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="bg-neutral-950 border-neutral-800 mt-1"
                        placeholder="+44 123 456 7890"
                        required
                        data-testid="booking-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <MapPin size={18} className="text-[#B8960B]" />
                    Property Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-neutral-400 text-sm">Property Address *</Label>
                      <Input 
                        value={formData.property_address}
                        onChange={(e) => setFormData({...formData, property_address: e.target.value})}
                        className="bg-neutral-950 border-neutral-800 mt-1"
                        placeholder="Full property address"
                        required
                        data-testid="booking-address-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-neutral-400 text-sm">Postcode</Label>
                        <Input 
                          value={formData.postcode}
                          onChange={(e) => setFormData({...formData, postcode: e.target.value})}
                          className="bg-neutral-950 border-neutral-800 mt-1"
                          placeholder="SW1A 1AA"
                        />
                      </div>
                      <div>
                        <Label className="text-neutral-400 text-sm">Property Type</Label>
                        <select
                          value={formData.property_type}
                          onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                          className="w-full bg-neutral-950 border border-neutral-800 rounded-md h-10 px-3 text-white mt-1"
                          data-testid="booking-property-type-select"
                        >
                          <option value="">Select...</option>
                          {propertyTypes.map(t => (
                            <option key={t.id} value={t.id}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-neutral-400 text-sm">Furnished Status</Label>
                      <select
                        value={formData.furnished}
                        onChange={(e) => setFormData({...formData, furnished: e.target.value})}
                        className="w-full bg-neutral-950 border border-neutral-800 rounded-md h-10 px-3 text-white mt-1"
                      >
                        <option value="">Select...</option>
                        {furnishedOptions.map(f => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Services */}
                <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Home size={18} className="text-[#B8960B]" />
                    Services Required *
                  </h3>
                  <div className="space-y-2">
                    {serviceOptions.map(service => (
                      <button
                        key={service.id}
                        type="button"
                        onClick={() => handleServiceToggle(service.id)}
                        className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-all ${
                          formData.services.includes(service.id)
                            ? 'border-[#B8960B] bg-[#B8960B]/10'
                            : 'border-neutral-800 hover:border-neutral-700'
                        }`}
                        data-testid={`booking-service-${service.id}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          formData.services.includes(service.id)
                            ? 'border-[#B8960B] bg-[#B8960B]'
                            : 'border-neutral-600'
                        }`}>
                          {formData.services.includes(service.id) && <Check size={12} className="text-white" />}
                        </div>
                        <span className="text-sm">{service.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-neutral-400 text-sm">Additional Notes</Label>
                  <Textarea 
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="bg-neutral-950 border-neutral-800 mt-1"
                    placeholder="Any special requirements or access instructions..."
                    rows={3}
                  />
                </div>

                {/* Quote Summary */}
                {quoteData && (
                  <div className="bg-[#B8960B]/10 border border-[#B8960B]/30 rounded-xl p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-neutral-300">Estimated Price</span>
                      <span className="text-2xl font-bold text-[#B8960B]">£{quoteData.total_price.toFixed(2)}</span>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isSubmitting || !selectedDate || !selectedTime}
                  className="btn-primary rounded-full w-full py-6 text-lg"
                  data-testid="confirm-booking-btn"
                >
                  {isSubmitting ? "Confirming..." : "Confirm Booking"}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
